import React from 'react';
import styled from 'styled-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import Chevron from '@narvar/nth-bit-icon-chevron';
import Label, { LabelAnimateState } from './Label';
import ErrorMessage from './ErrorMessage';

interface RegisteredSelectListProps {
  id: string;
  label: string;
  className?: string;
  containerOverridesStyle?: React.CSSProperties;
  value?: string;
  children?: React.ReactNodeArray;
  defaultValue?: string;
  errorMessage?: string;
  disabled?: boolean;
  animateLabel?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onFocus?: (event?: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLSelectElement>) => void;
  register: UseFormRegisterReturn;
  featureName?: string;
}

const disableSelect = (customStyles = {}): React.CSSProperties => ({
  borderColor: '#e0e0e0',
  color: '#e0e0e0',
  cursor: 'not-allowed',
  ...customStyles,
});

const Container = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const StyledSelect = styled.select<{
  s?: {
    sFocus?: React.CSSProperties;
    sDefault?: React.CSSProperties;
    sError?: React.CSSProperties;
    sContainerOverrides?: React.CSSProperties;
    inputActive?: boolean;
  };
}>(({ s }) => ({
  appearance: 'none',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: '6.25rem',
  borderStyle: 'solid',
  borderColor: '#999',
  borderWidth: '0.0625rem',
  borderRadius: '0.125rem',
  cursor: 'auto',
  fontSize: '0.825rem',
  color: '#000',
  // TODO: Styling native `select` elements is not fun
  //       Each browser adds a different amount of default
  //       "padding" which isn't actually padding, so this
  //       padding definition just adds to it.
  padding:
    s && s.inputActive
      ? '1.25rem 0.75rem 0.25rem 0.5rem'
      : '0.75rem 0.75rem 0.75rem 0.5rem',
  ...s?.sDefault,
  '&:focus': {
    borderColor: '#111',
    outlineWidth: '0',
    outline: 'none',
    ...(s && s.sError),
  },
  ...(s && s.sError),
  ...(s && s.sContainerOverrides),
}));

const RegisteredSelectList: React.FunctionComponent<
  RegisteredSelectListProps
> = ({
  className = '',
  id,
  label,
  animateLabel = false,
  errorMessage,
  containerOverridesStyle = {},
  value,
  defaultValue,
  disabled = false,
  onChange = (data) => data,
  onFocus = () => undefined,
  onBlur = () => undefined,
  children,
  featureName,
  register,
}) => {
  const errorId = `${id}-error`;
  const hasError = Boolean(errorMessage && errorMessage.length);
  const { onBlur: rOnBlur, onChange: rOnChange, ref, name } = register;
  const [inputActive, setInputActive] = React.useState(
    Boolean(value || defaultValue),
  );
  const [animateState, setAnimateState] = React.useState(
    LabelAnimateState.none,
  );

  const {
    inputtextinputsNoneDisabled: inputStyleDisabled,
    inputtextinputsNoneFocus: inputStyleFocus,
    inputtextinputsNoneNone: inputStyle,
    captiontextErrorNone: captionTextError,
  } = useTheme();

  const errorColor = (captionTextError as { color?: string } | undefined)
    ?.color;

  React.useEffect(() => {
    if (!animateLabel) return;
    setAnimateState(
      inputActive ? LabelAnimateState.active : LabelAnimateState.inactive,
    );
  }, [inputActive, animateLabel]);

  return (
    <>
      <Container>
        <Label htmlFor={id} labelState={animateState} featureName={featureName}>
          {label}
        </Label>
        <StyledSelect
          id={id}
          ref={ref}
          name={name}
          className={className}
          s={{
            sError:
              hasError && errorColor
                ? { border: `1px solid ${errorColor}` }
                : {},
            sFocus: inputStyleFocus,
            sDefault: disabled
              ? disableSelect({ inputStyleDisabled })
              : inputStyle,
            sContainerOverrides: containerOverridesStyle,
            inputActive,
          }}
          defaultValue={defaultValue}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            if (onChange) onChange(e);
            setInputActive(true);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            rOnChange(e);
          }}
          onFocus={(): void => {
            setInputActive(true);
            if (onFocus) onFocus();
          }}
          onBlur={(e) => {
            setInputActive(Boolean(value));
            if (onBlur) onBlur(e);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            rOnBlur(e);
          }}
        >
          {children}
        </StyledSelect>
        <Chevron
          s={{
            transform: 'translateY(-50%) rotate(-90deg)',
            position: 'absolute',
            top: '50%',
            right: '1rem',
            width: '.75rem',
            pointerEvents: 'none',
          }}
        />
      </Container>
      <ErrorMessage id={errorId} featureName={featureName} show={hasError}>
        {errorMessage ?? ''}
      </ErrorMessage>
    </>
  );
};

export default withThemeProvider(RegisteredSelectList, {
  themeTokens: [
    'inputtextinputsNoneDisabled',
    'inputtextinputsNoneFocus',
    'inputtextinputsNoneNone',
    'captiontextErrorNone',
  ],
});
